import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  Database, CheckCircle, Clock, Shield, ShieldAlert, AlertTriangle,
  Play, RefreshCw, X, ChevronDown, ChevronRight, Loader,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SAFETY_BADGES = {
  safe_additive: { bg: '#052e16', border: '#166534', color: '#4ade80', label: 'SAFE (additive)' },
  review_required: { bg: '#1c1917', border: '#854d0e', color: '#fbbf24', label: 'REVIEW REQUIRED' },
  blocked: { bg: '#1c0a0a', border: '#991b1b', color: '#f87171', label: 'BLOCKED' },
};

export default function AdminSchema() {
  const [password, setPassword] = useState(sessionStorage.getItem('admin_password') || '');
  const [authenticated, setAuthenticated] = useState(!!sessionStorage.getItem('admin_password'));
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Apply state
  const [applyingVersion, setApplyingVersion] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  const [applyError, setApplyError] = useState(null);

  // Expansion
  const [expanded, setExpanded] = useState(new Set());

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-Admin-Password': password || sessionStorage.getItem('admin_password') || '',
  }), [password]);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/migrations/status`, { headers: headers() });
      let text = '';
      try { text = await resp.clone().text(); } catch { try { text = await resp.text(); } catch { /* */ } }
      let data = {};
      try { data = JSON.parse(text); } catch { /* */ }

      if (resp.status === 401) { handleLogout(); return; }
      if (!resp.ok) throw new Error(data.detail || `HTTP ${resp.status}`);
      setStatus(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const handleLogin = (e) => {
    e.preventDefault();
    sessionStorage.setItem('admin_password', password);
    setAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    setPassword('');
    setAuthenticated(false);
    setStatus(null);
  };

  useEffect(() => {
    if (authenticated) fetchStatus();
  }, [authenticated, fetchStatus]);

  const applyMigration = useCallback(async (version) => {
    setApplyResult(null);
    setApplyError(null);
    setApplyingVersion(version);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/migrations/apply`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          version,
          confirm: confirmText,
        }),
      });
      let text = '';
      try { text = await resp.clone().text(); } catch { try { text = await resp.text(); } catch { /* */ } }
      let data = {};
      try { data = JSON.parse(text); } catch { /* */ }

      if (!resp.ok) throw new Error(data.detail || `HTTP ${resp.status}`);
      setApplyResult(data);
      setConfirmText('');
      // Refresh status
      fetchStatus();
    } catch (e) {
      setApplyError(e.message);
    } finally {
      setApplyingVersion(null);
    }
  }, [headers, confirmText, fetchStatus]);

  const toggleExpand = (v) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  // ── Login ──
  if (!authenticated) {
    return (
      <AdminLayout activePage="schema" onLogout={handleLogout}>
        <div style={S.centered}>
          <form onSubmit={handleLogin} style={S.loginCard}>
            <Database size={28} color="#f59e0b" />
            <h2 style={S.loginTitle}>Schema Migrations</h2>
            <input
              type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Admin password"
              style={S.input} autoFocus
            />
            <button type="submit" style={S.primaryBtn}>Authenticate</button>
          </form>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activePage="schema" onLogout={handleLogout}>
      <div style={S.page}>
        {/* Header */}
        <div style={S.header}>
          <div>
            <h1 style={S.title}>Schema Migrations</h1>
            {status && (
              <span style={S.subtitle}>
                Current: v{status.current_version} · {status.total_applied} applied · {status.total_pending} pending
              </span>
            )}
          </div>
          <button onClick={fetchStatus} disabled={loading} style={S.refreshBtn}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Checking…' : 'Refresh'}
          </button>
        </div>

        {error && <div style={S.errorBanner}>{error}</div>}

        {/* Apply result */}
        {applyResult && (
          <div style={{
            ...S.card, borderColor: applyResult.status === 'applied' ? '#166534' : '#854d0e',
            marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <CheckCircle size={20} color="#4ade80" />
            <div>
              <div style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>
                {applyResult.status === 'already_applied'
                  ? `v${applyResult.version} already applied`
                  : `v${applyResult.version} applied successfully`}
              </div>
              <div style={{ color: '#737373', fontSize: 12 }}>
                {applyResult.description || applyResult.message}
                {applyResult.duration_ms != null && ` · ${applyResult.duration_ms}ms`}
              </div>
            </div>
            <button onClick={() => setApplyResult(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#525252', cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>
        )}

        {applyError && (
          <div style={{ ...S.errorBanner, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={14} />
            {applyError}
            <button onClick={() => setApplyError(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer' }}>
              <X size={14} />
            </button>
          </div>
        )}

        {status && (
          <>
            {/* Pending Migrations */}
            {status.pending.length > 0 && (
              <div style={{ ...S.card, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <Clock size={16} color="#f59e0b" />
                  <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>
                    Pending Migrations ({status.total_pending})
                  </span>
                </div>

                {status.pending.map(m => {
                  const isExpanded = expanded.has(m.version);
                  const badge = SAFETY_BADGES[m.safety] || SAFETY_BADGES.blocked;
                  const expectedConfirm = `APPLY V${m.version}`;
                  const isBlocked = m.safety === 'blocked';

                  return (
                    <div key={m.version} style={S.migrationRow}>
                      {/* Header */}
                      <div
                        style={S.migrationHeader}
                        onClick={() => toggleExpand(m.version)}
                      >
                        {isExpanded
                          ? <ChevronDown size={14} color="#737373" />
                          : <ChevronRight size={14} color="#737373" />}
                        <span style={S.versionBadge}>v{m.version}</span>
                        <span style={{ color: '#e5e5e5', fontSize: 13, fontWeight: 500, flex: 1 }}>
                          {m.name}
                        </span>
                        <span style={{
                          padding: '2px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                          letterSpacing: 0.5, background: badge.bg,
                          border: `1px solid ${badge.border}`, color: badge.color,
                        }}>
                          {badge.label}
                        </span>
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div style={S.migrationDetail}>
                          <div style={S.detailRow}>
                            <span style={S.detailLabel}>Description</span>
                            <span style={S.detailValue}>{m.description}</span>
                          </div>
                          <div style={S.detailRow}>
                            <span style={S.detailLabel}>Required by</span>
                            <span style={{ ...S.detailValue, color: '#f59e0b' }}>{m.required_by}</span>
                          </div>
                          <div style={S.detailRow}>
                            <span style={S.detailLabel}>Objects</span>
                            <span style={S.detailValue}>
                              {m.objects.map((o, i) => (
                                <span key={i} style={S.objectTag}>{o}</span>
                              ))}
                            </span>
                          </div>
                          {m.safety_reasons && m.safety_reasons.length > 0 && (
                            <div style={S.detailRow}>
                              <span style={S.detailLabel}>Safety</span>
                              <span style={{ ...S.detailValue, fontSize: 11, color: badge.color }}>
                                {m.safety_reasons.join('; ')}
                              </span>
                            </div>
                          )}

                          {/* Apply control */}
                          {!isBlocked && (
                            <div style={S.applyBox}>
                              <div style={{ color: '#737373', fontSize: 11, marginBottom: 6 }}>
                                Type <span style={{ color: '#f59e0b', fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
                                  {expectedConfirm}
                                </span> to apply:
                              </div>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={confirmText}
                                  onChange={e => setConfirmText(e.target.value)}
                                  placeholder={expectedConfirm}
                                  style={{ ...S.input, flex: 1, fontSize: 13 }}
                                  onFocus={() => setConfirmText('')}
                                />
                                <button
                                  onClick={() => applyMigration(m.version)}
                                  disabled={confirmText !== expectedConfirm || applyingVersion === m.version}
                                  style={{
                                    padding: '8px 20px', borderRadius: 6, fontWeight: 600, fontSize: 13,
                                    border: '1px solid',
                                    cursor: confirmText === expectedConfirm ? 'pointer' : 'not-allowed',
                                    borderColor: confirmText === expectedConfirm ? '#166534' : '#333',
                                    background: confirmText === expectedConfirm ? '#052e16' : '#0a0a0a',
                                    color: confirmText === expectedConfirm ? '#4ade80' : '#525252',
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    opacity: applyingVersion === m.version ? 0.6 : 1,
                                  }}
                                >
                                  {applyingVersion === m.version
                                    ? <><Loader size={13} className="animate-spin" /> Applying…</>
                                    : <><Play size={13} /> Apply v{m.version}</>}
                                </button>
                              </div>
                            </div>
                          )}

                          {isBlocked && (
                            <div style={{ ...S.applyBox, borderColor: '#991b1b', background: '#1c0a0a' }}>
                              <ShieldAlert size={14} color="#f87171" />
                              <span style={{ color: '#f87171', fontSize: 12 }}>
                                This migration contains destructive operations and cannot be applied from the admin UI.
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {status.pending.length === 0 && (
              <div style={{ ...S.card, textAlign: 'center', padding: 40, marginBottom: 16 }}>
                <Shield size={32} color="#4ade80" style={{ margin: '0 auto 12px' }} />
                <div style={{ color: '#4ade80', fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                  Schema Up to Date
                </div>
                <div style={{ color: '#525252', fontSize: 12 }}>
                  All {status.total_applied} migrations applied. Current version: v{status.current_version}.
                </div>
              </div>
            )}

            {/* Applied Migrations */}
            <div style={S.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <CheckCircle size={16} color="#4ade80" />
                <span style={{ color: '#e5e5e5', fontSize: 14, fontWeight: 600 }}>
                  Applied ({status.total_applied})
                </span>
              </div>
              <div style={S.tableContainer}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Version</th>
                      <th style={S.th}>Name</th>
                      <th style={S.th}>Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.applied.map((m, idx) => (
                      <tr key={m.version} style={idx % 2 === 0 ? S.trEven : {}}>
                        <td style={S.td}><span style={S.versionBadge}>v{m.version}</span></td>
                        <td style={S.td}>{m.name}</td>
                        <td style={S.td}>{m.applied_at ? _timeAgo(m.applied_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function _timeAgo(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

const S = {
  page: { padding: '24px 32px', maxWidth: 960, margin: '0 auto' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20,
  },
  title: { color: '#e5e5e5', fontSize: 20, fontWeight: 700, margin: 0 },
  subtitle: { color: '#525252', fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', background: '#1a1a1b', border: '1px solid #333',
    borderRadius: 6, color: '#a3a3a3', fontSize: 12, cursor: 'pointer',
  },
  errorBanner: {
    background: '#1c0a0a', border: '1px solid #991b1b', borderRadius: 8,
    padding: '10px 16px', color: '#fca5a5', fontSize: 12, marginBottom: 16,
    display: 'flex', alignItems: 'center', gap: 8,
  },
  card: {
    background: '#141415', border: '1px solid #1f1f1f', borderRadius: 10,
    padding: 20,
  },
  migrationRow: {
    borderRadius: 8, overflow: 'hidden', marginBottom: 10,
    border: '1px solid #1f1f1f', background: '#0a0a0a',
  },
  migrationHeader: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
    cursor: 'pointer', userSelect: 'none',
  },
  migrationDetail: { padding: '0 16px 16px', borderTop: '1px solid #1f1f1f' },
  detailRow: {
    display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #1a1a1b',
    alignItems: 'flex-start',
  },
  detailLabel: {
    width: 100, minWidth: 100, color: '#525252', fontSize: 11,
    textTransform: 'uppercase', letterSpacing: 0.5, paddingTop: 2,
  },
  detailValue: { color: '#a3a3a3', fontSize: 12, flex: 1, lineHeight: 1.6 },
  objectTag: {
    display: 'inline-block', padding: '1px 8px', borderRadius: 4,
    fontSize: 10, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace",
    background: '#1a1a1b', border: '1px solid #262626', color: '#737373',
    marginRight: 4, marginBottom: 4,
  },
  versionBadge: {
    display: 'inline-block', padding: '1px 8px', borderRadius: 4,
    fontSize: 11, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
    background: '#0a0a0a', border: '1px solid #333', color: '#f59e0b',
  },
  applyBox: {
    display: 'flex', flexDirection: 'column', gap: 6,
    marginTop: 12, padding: '12px 14px',
    background: '#0a0a0a', border: '1px solid #1f1f1f', borderRadius: 8,
  },
  primaryBtn: {
    width: '100%', padding: '10px 20px', borderRadius: 8, fontWeight: 600,
    background: '#f59e0b', color: '#000', border: 'none',
    cursor: 'pointer', fontSize: 14,
  },
  input: {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    background: '#0a0a0a', border: '1px solid #333', color: '#e5e5e5',
    fontSize: 14, outline: 'none',
  },
  centered: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '70vh',
  },
  loginCard: {
    background: '#141415', border: '1px solid #1f1f1f', borderRadius: 12,
    padding: 32, width: 340, display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 16,
  },
  loginTitle: { color: '#e5e5e5', fontSize: 18, fontWeight: 700, margin: 0 },
  tableContainer: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '8px 12px', fontSize: 10,
    textTransform: 'uppercase', letterSpacing: 0.5,
    color: '#525252', borderBottom: '1px solid #1f1f1f',
  },
  td: {
    padding: '8px 12px', fontSize: 12, color: '#a3a3a3',
    borderBottom: '1px solid #0f0f0f',
  },
  trEven: { background: 'rgba(255,255,255,0.015)' },
};
